import type { HTMLProps, ReactNode } from 'react';
import { forwardRef } from 'react';

import { useMaskLink } from '@tmh-website/services/link.service';
import { Color } from '@tmh-website/shared/colors';
import { ButtonThemeColor, buttonThemes } from '@tmh-website/ui/Button';
import LoadingSpinner from '@tmh-website/ui/LoadingSpinner';

import * as Styled from './ButtonIcon.styles';

export interface ButtonIconProps
  extends Omit<HTMLProps<HTMLButtonElement | HTMLAnchorElement>, 'type' | 'ref' | 'as' | 'forwardedAs'> {
  children: ReactNode;
  themeColor?: ButtonThemeColor;
  isTransparent?: boolean;
  ref?: any;
  type?: string;
  isLoading?: boolean;
  isSmall?: boolean;
  href?: string;
  /**
   * Mask the link using base64 encoding. This is used for SEO optimization
   */
  maskLink?: boolean;
  /**
   * Sometimes this button still uses phosphor icons. Those usually have a size of 24px.
   * This adds a padding to the button to align its size with the TMH icon size.
   */
  phosphorPadding?: boolean;
}

/**
 * A button that has a solid background hover effect.
 * Usually used with icons only but can be combined with text.
 */
const ButtonIcon = forwardRef<HTMLButtonElement | HTMLAnchorElement, ButtonIconProps>(
  (
    {
      children,
      themeColor = Color.Blue_500_Ultramarine,
      isTransparent = false,
      isLoading,
      isSmall = false,
      type,
      href,
      maskLink = false,
      phosphorPadding,
      ...props
    },
    ref
  ) => {
    const { renderedHref, onMouseEnter, onMouseLeave } = useMaskLink(maskLink, href);

    return (
      // @ts-ignore - FIXME: There are some type errors, probably with the `ref` property.
      <Styled.Container
        type={type}
        // @ts-ignore - FIXME: There are some type errors, probably with the `ref` property.
        ref={ref}
        as={renderedHref ? 'a' : 'button'}
        href={renderedHref}
        $phosphorPadding={phosphorPadding}
        {...props}
        onMouseEnter={e => {
          if (onMouseEnter) onMouseEnter();
          if (props.onMouseEnter) props.onMouseEnter(e);
        }}
        onMouseLeave={e => {
          if (onMouseLeave) onMouseLeave();
          if (props.onMouseLeave) props.onMouseLeave(e);
        }}
        $themeColor={themeColor}
        $isLoading={isLoading}
        $isSmall={isSmall}
        $isTransparent={isTransparent}>
        {children}
        {isLoading && <LoadingSpinner color={buttonThemes[themeColor].text} absolutePosition />}
      </Styled.Container>
    );
  }
);

ButtonIcon.displayName = 'ButtonIcon';

export default ButtonIcon;
